import { useState, useCallback } from 'react';
import classNames from 'classnames';
import { IconBtn } from './icon-btn';
import { BackIcon, InfoIcon } from './bs-icons';
import { LinearGradientColorBgAnimation } from './linear-gradient-color-bg-animation';
import { ConfirmDialog } from './confirm-dialog';
import { Spinner } from './ui/spinner';
import { BaseButton } from './base-button';
import { BrushIcon } from './bs-icons';
import { cn } from '../lib/utils';

export interface PreviewerHeaderProps {
  title: string;
  tips?: string;
  active?: boolean;
  className?: string;
  onBackClick?: () => void;
  onSave?: (isConfirm?: boolean) => void;
  onCancel?: () => void;
  onClear?: () => void;
  style?: React.CSSProperties;

  confirmTitle?: string;
  confirmContent?: string;
  dialogContainer?: HTMLElement;
  variant?: 'fields-previewer' | 'data-previewer';
  closeBtnLabel?: string;

  disableConfirm?: boolean;

  disableOperation?: boolean;

  customOperationElement?: React.ReactNode;

}

export const PreviewerHeader = ({
  title,
  tips,
  style,
  confirmContent,
  confirmTitle,
  className,
  active,
  onBackClick,
  onSave,
  closeBtnLabel,
  onCancel,
  onClear,
  variant = 'fields-previewer',
  dialogContainer,
  disableConfirm,
  disableOperation,
  customOperationElement,
}: PreviewerHeaderProps) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [loading, setLoading] = useState(false);

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  const setRootRef = useCallback((node: HTMLElement | null) => {
    if (!node) {
      setPortalContainer(null);
      return;
    }
    const rootNode = node.getRootNode();
    if (rootNode instanceof ShadowRoot) {
      const existing = rootNode.querySelector<HTMLElement>('[data-bs-dialog-portal]');
      if (existing) {
        setPortalContainer(existing);
        return;
      }
      const container = document.createElement('div');
      container.setAttribute('data-bs-dialog-portal', 'true');
      rootNode.appendChild(container);
      setPortalContainer(container);
    } else {
      setPortalContainer(null);
    }
  }, []);

  const btnResetStyle = classNames(
    'w-[94px] h-[34px] flex items-center justify-center rounded-[4px]'
  );

  const isData = variant === 'data-previewer';
  const isFields = variant === 'fields-previewer';

  const titleCls = classNames('font-[700]', {
    'text-[16px]': isFields,
    'text-[18px]': isData,
  });

  const effectiveContainer = dialogContainer || portalContainer || undefined;

  const operations = customOperationElement ? (
    customOperationElement
  ) : (
      <div className="flex items-center gap-[10px]">
        {onClear && (
          <BaseButton
            className={cn('w-[34px] h-[34px] text-[#8d8d8d] p-0 flex items-center justify-center hover:border-[#e0e0e0] hover:text-[#8d8d8d] border border-solid border-[#e0e0e0] ', {
                'pointer-events-none opacity-50': disableOperation || loading,
            })}
            tooltip='清除全部'
            icon={<BrushIcon />}
            onClick={() => {
              onClear?.();
            }}
          />
        )}

        {onSave && (
          <button
            onClick={async () => {
              setLoading(true);
              try {
                await onSave();
              } catch (error) {
                console.error('Save failed:', error);
              } finally {
                setLoading(false);
              }
            }}
            className={classNames(
              btnResetStyle,
              'flex items-center gap-[6px]',
              'bg-[#0265FF] hover:opacity-90 text-white text-[14px]',
              {
                'w-[115px]': isData,
                'pointer-events-none opacity-50': disableOperation || loading,
              }
            )}
          >
            {loading ? <Spinner /> : null}
            {isFields ? '保存' : '添加到表单'}
          </button>
        )}
        {onCancel && (
          <button
            onClick={() => {
              // if (disableOperation) {
              //   return;
              // }

              if (disableConfirm) {
                onCancel?.();
                onBackClick?.();
                return;
              }

              setShowConfirmDialog(true);
            }}
            className={classNames(
              btnResetStyle,
              'text-[#121111] text-[14px] bg-[#fff] border border-solid border-[#e0e0e0] hover:bg-[#f0f0f0]',
              {
                'pointer-events-none opacity-50': loading,
              }
            )}
          >
            {closeBtnLabel || '取消'}
          </button>
        )}
      </div>
  )

  const contentJsx = (
    <>
      <div className="flex items-center gap-3">
        {isData ? null : (
          <IconBtn
            icon={<BackIcon />}
            onClick={() => {
              // if (disableOperation) {
              //   return;
              // }

              if (disableConfirm) {
                onCancel?.();
                onBackClick?.();
                return;
              }

              setShowConfirmDialog(true);
            }}
            className="text-[#0265FF] hover:text-[#0265FF]"
          />
        )}
        <span className={titleCls}>{title}</span>
        {tips && active && (
          <div className="flex items-center gap-[6px] text-[#999999] text-[12px]">
            <InfoIcon />
            {tips}
          </div>
        )}
      </div>
      {operations}
      <ConfirmDialog
        container={effectiveContainer}
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        title={confirmTitle || 'AI表单搭建有修改，是否保存？'}
        description={confirmContent || '您修改了AI表单搭建但没有保存，是否需要保存并继续？'}
        onLinkClick={() => setShowConfirmDialog(false)}
        onCancel={() => {
          setShowConfirmDialog(false);
          onCancel?.();
          onBackClick?.();
        }}
        onConfirm={async () => {
          setShowConfirmDialog(false);
          setLoading(true);
          try {
            await onSave?.(true);
          } catch (error) {
            console.error('Save failed:', error);
          } finally {
            setLoading(false);
          }
        }}
      />
    </>
  );

  const borderCls = classNames({
    'border-b border-solid border-[#e0e0e0]': !active && isFields,
    'border-none': isData,
  });

  if (active) {
    return (
      <div ref={setRootRef}>
        <LinearGradientColorBgAnimation
          height={52}
          style={style}
          childrenContainerStyle={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 20px',
          }}
        >
          {contentJsx}
        </LinearGradientColorBgAnimation>
      </div>
    );
  }

  const paddingCls = classNames({
    'px-[20px]': isFields,
    'px-[10px]': isData,
  });

  return (
    <div
      ref={setRootRef}
      style={style}
      className={classNames(
        'w-full h-[52px] flex items-center justify-between',
        paddingCls,
        className,
        borderCls
      )}
    >
      {contentJsx}
    </div>
  );
};
